/**
 * Dashboard TanStack Query Hook (T055)
 * Auto-refetch every 60 seconds per FR-011
 */

import { useQuery } from '@tanstack/react-query';
import apiClient, { type ApiResponse } from '../lib/api-client';

export interface DashboardData {
  today: {
    date: string;
    appointments: {
      total: number;
      upcoming: number;
      completed: number;
      cancelled: number;
      list: Array<{
        id: string;
        customerId: string;
        customerName: string;
        customerEmail: string;
        customerPhone: string | null;
        title: string;
        startTime: string;
        endTime: string;
        duration: number;
        status: string;
        location: string | null;
        meetingUrl: string | null;
        hasRecording: boolean;
        hasInvoice: boolean;
      }>;
    };
  };
  invoices: {
    pending: {
      count: number;
      total: number;
      totalFormatted: string;
      list: Array<{
        id: string;
        invoiceNumber: string;
        customerId: string;
        customerName: string;
        total: number;
        totalFormatted: string;
        dueDate: string;
        status: string;
        isOverdue: boolean;
      }>;
    };
    overdue: {
      count: number;
      total: number;
      totalFormatted: string;
      list: Array<{
        id: string;
        invoiceNumber: string;
        customerId: string;
        customerName: string;
        total: number;
        totalFormatted: string;
        dueDate: string;
        daysPastDue: number;
      }>;
    };
  };
  payments: {
    recent: {
      count: number;
      total: number;
      totalFormatted: string;
      list: Array<{
        id: string;
        customerId: string;
        customerName: string;
        invoiceNumber: string;
        amount: number;
        amountFormatted: string;
        method: string;
        status: string;
        paidAt: string | null;
      }>;
    };
  };
  metrics: {
    thisMonth: {
      revenue: number;
      revenueFormatted: string;
      expenses: number;
      expensesFormatted: string;
      netRevenue: number;
      netRevenueFormatted: string;
      appointments: number;
      newCustomers: number;
      pendingInvoicesCount: number;
      pendingInvoicesTotal: number;
      pendingInvoicesTotalFormatted: string;
    };
    thisWeek: {
      appointments: number;
      revenue: number;
      revenueFormatted: string;
    };
  };
  recentActivity: Array<{
    id: string;
    type: 'appointment' | 'invoice' | 'payment' | 'customer';
    action: string;
    description: string;
    timestamp: string;
    icon: string;
    metadata?: Record<string, unknown>;
  }>;
}

export function useDashboard() {
  return useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<DashboardData>>('/dashboard');
      return response.data.data;
    },
    refetchInterval: 60_000, // 60s auto-refetch per FR-011
    staleTime: 30_000,
  });
}

export function useDashboardQuick() {
  return useQuery({
    queryKey: ['dashboard', 'quick'],
    queryFn: async () => {
      const response =
        await apiClient.get<ApiResponse<Record<string, unknown>>>('/dashboard/quick');
      return response.data.data;
    },
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}
